import type { SupabaseClient } from '../client'
import type { ClientUser, ClientUserProfile } from '../domain/client-user'
import { SupabaseError } from '../errors'
import { unwrapMaybe } from './_helpers'

const PROFILE_COLUMNS = 'id, name, email, phone, company, avatar_url, role, active'
const AVATAR_BUCKET = 'client-avatars'

/**
 * Busca o profile do cliente pelo auth.uid().
 * Retorna `null` se o auth_user_id não foi vinculado a nenhum client_user.
 */
export async function getClientUserByAuthId(
  client: SupabaseClient,
  authUserId: string,
): Promise<ClientUserProfile | null> {
  const result = await client
    .schema('portal')
    .from('client_users')
    .select(PROFILE_COLUMNS)
    .eq('auth_user_id', authUserId)
    .maybeSingle()
  return unwrapMaybe(result) as unknown as ClientUserProfile | null
}

/**
 * Atualiza o timestamp do último login. Fire-and-forget.
 */
export async function touchLastLogin(
  client: SupabaseClient,
  clientUserId: ClientUser['id'],
): Promise<void> {
  await client
    .schema('portal')
    .from('client_users')
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', clientUserId)
}

export type UpdateClientProfileInput = {
  name?: string
  phone?: string | null
  company?: string | null
}

/**
 * Atualiza campos editáveis pelo próprio cliente.
 */
export async function updateClientProfile(
  client: SupabaseClient,
  clientUserId: string,
  fields: UpdateClientProfileInput,
): Promise<void> {
  const { error } = await client
    .schema('portal')
    .from('client_users')
    .update(fields)
    .eq('id', clientUserId)
  if (error) throw new SupabaseError(error.message, error)
}

/**
 * Faz upload do avatar para o Storage e atualiza `client_users.avatar_url`.
 * Retorna a URL pública do avatar.
 */
export async function uploadClientAvatar(
  client: SupabaseClient,
  clientUserId: string,
  file: File,
): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png'
  const path = `${clientUserId}/avatar_${Date.now()}.${ext}`

  const { error: uploadError } = await client.storage
    .from(AVATAR_BUCKET)
    .upload(path, file, { upsert: true })
  if (uploadError) throw new SupabaseError(uploadError.message, uploadError)

  const publicUrl = client.storage.from(AVATAR_BUCKET).getPublicUrl(path).data.publicUrl

  const { error: updateError } = await client
    .schema('portal')
    .from('client_users')
    .update({ avatar_url: publicUrl })
    .eq('id', clientUserId)
  if (updateError) throw new SupabaseError(updateError.message, updateError)

  return publicUrl
}
